import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import md5 from 'crypto-js/md5';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';
import CalendarInline from '../../components/calendar/new';
import SearchVisitor from '../../components/dyrejournal/searchVisitor';
import SearchAnimal from '../../components/dyrejournal/searchAnimal';

function getNextQuarterHour(addMinutes = 0) {
    const now = new Date();
    let minutes = now.getMinutes();
    let quarterHours = Math.ceil(minutes / 15) * 15;
    if (quarterHours === 60) { // If it's on the hour, move to the next hour
        now.setHours(now.getHours() + 1);
        quarterHours = 0;
    }
    now.setMinutes(quarterHours);
    now.setSeconds(0);
    now.setMilliseconds(0);

    if (addMinutes) {
        now.setMinutes(now.getMinutes() + addMinutes);
    }
    return util.isoDate(now);
}

@withText(props => ({
    calendarEventNumber: <Text id='input.calendarEventNumber-help'>calendarEventNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    url: <Text id='input.url-help'>url</Text>,
}))
@observer
class NewCalendarEvent extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('calendarEvent'),
            selectedCalendarIds: [],
        };
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id, calendarType, skipAnimal, visitors = [], animals = [] } = props;
        const { calendarEventStore, calendarTemplateStore } = this.props.stores;
        if (emptyFirst) {
            calendarEventStore.updateKeyValue('calendarEvent', {});
        }
        // console.log('loadAll', { id, calendarType, skipAnimal })
        if (id) {
            await calendarEventStore.load(id);
        }
        await this.loadCalendarType(props);

        // Load calendar templates
        await calendarTemplateStore.load({ query: {} });

        const hasAnimals = animals && animals.length > 0;
        const hasVisitors = visitors && visitors.length > 0;
        if (hasAnimals) {
            this.insertAnimal();
        } else if (hasVisitors) {
            this.insertVisitor();
        }
        this.insertDates();
    }

    loadCalendarType = async (props = this.props) => {
        const { id, calendarType = 'booking', calendarId } = props;
        const { calendarStore, calendarEventStore } = this.props.stores;
        await calendarStore.load({ query: { type: calendarType } });

        const { calendars } = calendarStore;
        const calendar = calendars[0];
        const initialCalendarId = calendarId || calendar.calendarId;
        calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'calendarId', initialCalendarId);
        calendarEventStore.updateKeyValue('calendars', calendars);

        // Pre-select the current calendar for multi-booking
        if (initialCalendarId) {
            this.setState({ selectedCalendarIds: [initialCalendarId] });
        }
    }

    insertVisitor = (props = this.props) => {
        const { visitors = [], calendarType, skipVisitor } = props;
        const { calendarEventStore } = this.props.stores;

        const calendarVisitors = visitors.map((visitor) => ({
            md5: md5(JSON.stringify(visitor)).toString(),
            id: visitor.id,
            firstname: visitor.firstname,
            lastname: visitor.lastname,
            cellphone: visitor.cellphone,
            email: visitor.email,
        }));
        if (skipVisitor) {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'visitors', []);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'title', `${calendarType === 'booking' ? 'Ny time' : 'Innkalling'}`);
        } else {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'visitors', calendarVisitors);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'title', `${calendarType === 'booking' ? '' : 'Innkalling for'} ${visitors.map(visitor => `${visitor.firstname} ${visitor.lastname}`).join(', ')}`);
        }
    }

    insertAnimal = (props = this.props) => {
        const { animals = [], calendarType, skipAnimal } = props;
        const { calendarEventStore } = this.props.stores;

        const calendarAnimals = animals.map((animal) => ({
            md5: md5(JSON.stringify(animal)).toString(),
            id: animal.id,
            name: animal.name,
            birth: animal.birth,
            breed: animal.breed,
            species: animal.species,
        }));
        if (skipAnimal) {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'animals', []);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'title', `${calendarType === 'booking' ? 'Ny time' : 'Innkalling'}`);
        } else {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'animals', calendarAnimals);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'title', `${calendarType === 'booking' ? '' : 'Innkalling for'} ${animals.map(animal => `${animal.name}`).join(', ')}`);
        }
    }

    insertDates = (props = this.props) => {
        const { startDate = getNextQuarterHour(), endDate = getNextQuarterHour(15), calendarType, calendarId } = props;
        const { calendarEventStore } = this.props.stores;

        let oneYearFromNow;
        if (calendarType === 'notification') {
            oneYearFromNow = util.addDays(startDate, 365);
            const oneYearFromNowPluss1Hour = util.addSeconds(oneYearFromNow, 900);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'start', oneYearFromNow);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'end', oneYearFromNowPluss1Hour);
        } else {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'start', startDate);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'end', endDate);
        }
    }

    applyTemplate = (e) => {
        const { id } = e.target.closest('.template-item').dataset;
        const { calendarEventStore, calendarTemplateStore } = this.props.stores;
        const { calendarTemplates } = calendarTemplateStore;
        const { newCalendarEvent } = calendarEventStore;

        const template = calendarTemplates.find(t => t.id === parseInt(id, 10));
        if (!template) return;

        // Get current start time or default to next quarter hour
        const start = newCalendarEvent.start || getNextQuarterHour();
        const startDate = new Date(start);

        // Calculate end time based on template duration
        const durationMinutes = template.durationMinutes || 30;
        const endDate = new Date(startDate.getTime() + (durationMinutes * 60 * 1000));

        // Apply template fields to the new calendar event
        if (template.title) {
            // Append template title to existing title if visitor/animal names are already set
            const existingTitle = newCalendarEvent.title || '';
            const baseTitle = existingTitle.replace(/^(Ny time|Innkalling for)\s*/, '').trim();
            const newTitle = baseTitle ? `${template.title} - ${baseTitle}` : template.title;
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'title', newTitle);
        }
        if (template.body) {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'body', template.body);
        }
        if (template.backgroundColor) {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'backgroundColor', template.backgroundColor);
        }
        if (template.color) {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'color', template.color);
        }
        if (template.smsText) {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'smsText', template.smsText);
        }

        // Set the calculated end time
        calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'end', util.isoDate(endDate));

        // Mark which template was applied (for UI feedback)
        this.setState({ appliedTemplateId: template.id });
    }

    toggleCalendarId = (e) => {
        const calendarId = e.target.value;
        const { calendarEventStore } = this.props.stores;
        this.setState(prevState => {
            const { selectedCalendarIds } = prevState;
            let newIds;
            if (selectedCalendarIds.includes(calendarId)) {
                newIds = selectedCalendarIds.filter(id => id !== calendarId);
            } else {
                newIds = [...selectedCalendarIds, calendarId];
            }
            // Update the inline calendar preview to the first selected calendar
            if (newIds.length > 0) {
                calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'calendarId', newIds[0]);
            }
            return { selectedCalendarIds: newIds };
        });
    }

    toggleAllCalendars = () => {
        const { calendarEventStore } = this.props.stores;
        const { calendars } = calendarEventStore;
        this.setState(prevState => {
            const allIds = calendars.map(c => c.calendarId);
            const allSelected = allIds.every(id => prevState.selectedCalendarIds.includes(id));
            const newIds = allSelected ? [] : allIds;
            if (newIds.length > 0) {
                calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'calendarId', newIds[0]);
            }
            return { selectedCalendarIds: newIds };
        });
    }

    createCalendarEvent = async () => {
        const { callback = () => {}, installation } = this.props;
        const { appState, calendarEventStore, installationStore } = this.props.stores;
        const { newCalendarEvent } = calendarEventStore;
        const { selectedCalendarIds } = this.state;

        // Validate that end date is not before start date
        if (newCalendarEvent.start && newCalendarEvent.end) {
            const startDate = new Date(newCalendarEvent.start);
            const endDate = new Date(newCalendarEvent.end);
            if (endDate < startDate) {
                this.setState({ dateError: 'Sluttdato kan ikke være før startdato.' });
                return;
            }
        }
        this.setState({ dateError: null });

        // If multiple calendars selected, create one event per calendar
        const calendarIds = selectedCalendarIds.length > 1 ? selectedCalendarIds : [newCalendarEvent.calendarId];

        for (const calendarId of calendarIds) {
            const eventData = { ...newCalendarEvent, calendarId };
            const response = await calendarEventStore.insert(eventData);
            // Backend responds with a lowercase key (post-calendarevent.js).
            const calendarEvent = response?.data?.calendarevent;
            if (calendarEvent) {
                appState.trackCalendar('create', calendarEvent);
            }
        }

        calendarEventStore.updateKeyValue('newCalendarEvent', {});
        callback();
    }

    chooseItem = (e) => {
        const { id } = e.target.closest('.similar-item').dataset;
        const { calendarEventStore } = this.props.stores;
        const { foundList } = calendarEventStore;
        const item = foundList.find((item) => item.id === parseInt(id, 10));
        calendarEventStore.updateKeyValue('newCalendarEvent', {
            ...item,
        });
        calendarEventStore.resetSearch();
    }

    toggleSimilar = () => {
        const { showSimilar } = this.state;
        this.setState({
            showSimilar: !showSimilar,
        });
    }

    findAvailableTime = async () => {
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore } = this.props.stores;
		const { drawerHeightMedium } = appState;

		appState.openDrawerRight('calendar', {
			height: drawerHeightMedium,
            isNew: true,
            hideAdmin: true,
            callback: visitor => {
                appState.toggleDrawerRight(false, drawerLevel + 1);
                if (visitor) {
                    saleStore.updateObjectKeyValue('newSale', 'visitors', [visitor]);
                    setTimeout(() => {
                        this.loadAll(this.props, false);
                    }, 1000);
                }
            },
		}, drawerLevel + 2);
    }

    calendarSelectDateTime = ({ start, end, calendarId }) => {
        const { calendarEventStore } = this.props.stores;
        const { calendarEvent } = calendarEventStore;

        // Validate that end date is not before start date
        if (start && end) {
            const startDate = new Date(start);
            const endDate = new Date(end);
            if (endDate < startDate) {
                this.setState({ dateError: 'Sluttdato kan ikke være før startdato.' });
                return;
            }
        }
        this.setState({ dateError: null });

        if (calendarEvent.id) {
            // console.log('existing calendarEvent');
            // console.log({ start, end, calendarId });
            calendarEventStore.save({
                start,
                end,
                calendarId,
            }, calendarEvent.id);
            calendarEventStore.updateField(calendarEvent.id, 'start', start);
            calendarEventStore.updateField(calendarEvent.id, 'end', end);
            calendarEventStore.updateField(calendarEvent.id, 'calendarId', calendarId);
        } else {
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'start', start);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'end', end);
            calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'calendarId', calendarId);
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    openTemplateManager = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;

        appState.openDrawer('calendarTemplateList', {
            height: drawerHeightMedium,
            callback: () => {
                appState.toggleDrawer(false, drawerLevel + 1);
                // Reload templates after management
                const { calendarTemplateStore } = this.props.stores;
                calendarTemplateStore.load({ query: {} });
            },
        }, drawerLevel + 1);
    }

    render() {
        const { week, isNew, drawerLevel, installation, lookupArray, startDate, id } = this.props;
        const { sections, showSimilar, appliedTemplateId, dateError, selectedCalendarIds } = this.state;
        const { userStore, calendarEventStore, calendarTemplateStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin, customer = {} } = userStore;
        const { newCalendarEvent, foundList, foundListKeys, calendars } = calendarEventStore;
        const { calendarTemplates } = calendarTemplateStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const canManageTemplates = isAdmin || isCustomerAdmin;
        const allCalendarIds = calendars ? calendars.map(c => c.calendarId) : [];
        const allSelected = allCalendarIds.length > 0 && allCalendarIds.every(cid => selectedCalendarIds.includes(cid));
        const { featureFlags = {} } = customer;
        const hideCalendarInEventEditor = parseInt(featureFlags.hideCalendarInEventEditor, 10) === 1;
        const showCalendarWhenEditEvent = parseInt(featureFlags.showCalendarWhenEditEvent, 10) === 1;

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div
                    class='w-100 h-100'
                    style='
                        // max-width: 960px;
                        padding-top: 15px;
                        padding-bottom: 15px;
                    '
                >
                    <div class='font-weight-lighter px-3 mt-0 box-header'>
                        <Text id='calendarevent.title'>Kalender booking</Text>
                    </div>

                    {isNew && (calendarTemplates?.length > 0 || canManageTemplates) && <>
                        <div class='w-100 d-flex flex-column mb-3'>
                            <div class='mx-4 px-3 py-1'>
                                <small class='text-uppercase text-muted font-weight-lighter'>
                                    <Text id='calendarEvent.templates'>Templates</Text>
                                </small>
                            </div>
                            <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-3 px-3 py-2 d-flex flex-wrap align-items-center`} style='font-size: 1.0em; border-radius: 10px; gap: 8px;'>
                                {(!calendarTemplates || calendarTemplates.length === 0) && canManageTemplates && <>
                                    <small class='text-muted'>
                                        <Text id='calendarEvent.noTemplates'>No templates yet. Click "Manage" to create one.</Text>
                                    </small>
                                </>}
                                {calendarTemplates?.map((template) => {
                                    const isApplied = appliedTemplateId === template.id;
                                    const bgColor = template.backgroundColor || '#e9ecef';
                                    const textColor = template.color || '#333';
                                    return (
                                        <div
                                            class={`template-item py-2 px-3 rounded-pill ${isApplied ? 'border border-2 border-success' : ''}`}
                                            style={`background-color: ${bgColor}; color: ${textColor}; cursor: pointer; transition: transform 0.1s;`}
                                            data-id={template.id}
                                            onClick={this.applyTemplate}
                                        >
                                            <span class='me-1'>{template.name}</span>
                                            <small class='opacity-75'>({template.durationMinutes || 30} min)</small>
                                            {isApplied && <i class='fa-solid fa-check ms-2' />}
                                        </div>
                                    );
                                })}
                                {canManageTemplates && <>
                                    <div class='flex-fill' />
                                    <div
                                        class='py-2 px-3 rounded-pill'
                                        style='background-color: #e9ecef; color: #333; cursor: pointer;'
                                        onClick={this.openTemplateManager}
                                    >
                                        <i class='fa-solid fa-cog me-1' />
                                        <span><Text id='calendarEvent.manageTemplates'>Manage</Text></span>
                                    </div>
                                </>}
                            </div>
                        </div>
                    </>}

                    <div class='w-100 d-flex flex-sm-row flex-column mt-0'>
                        <div class='w-100 d-flex flex-column'>
                            {showSimilar && foundList?.length > 0 && <>
                                <div class='w-100 d-flex flex-column justify-content-start'>
                                    <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                                        <div class='mx-4 font-weight-lighter px-3 py-1 text-muted' >
                                            <small class='text-uppercase'>
                                                <Text id='calendarEvent.similar'>Similar calendarEvents</Text>
                                            </small>
                                        </div>
                                        <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-4 d-flex flex-column px-0 overflow-auto`} style='font-size: 1.1em; border-radius: 10px; max-height: 35vh;'>
                                            {foundList.map((item, idx) => {
                                                return(<>
                                                    <div class='d-flex flex-row justify-content-between align-items-center'>
                                                        <div
                                                            class='flex-fill similar-item py-2 px-3'
                                                            data-id={item.id}
                                                            onClick={this.chooseItem}
                                                        >
                                                            {item.name}
                                                            <span class='font-weight-lighter text-muted ms-3'>{item.calendarEventNumber}</span>
                                                        </div>
                                                        {/* {item.images[0] && <>
                                                            <div>
                                                                <div class=''>
                                                                    <img src={`${item.images[0].s3MediumLink}`} class='rounded-lg img-fluid' style='max-height: 45px;' />
                                                                </div>
                                                            </div>
                                                        </>} */}
                                                    </div>
                                                </>);
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </>}
                            {/* <button type='button' class='btn btn-success btn-sm ' onClick={this.findAvailableTime}>
                                <i class='fa-solid fa-circle-info' /> <Text id='calendarEvent.findAvailableTime'>Find available time</Text>
                            </button> */}

                            {isNew && <>
                                <div class='w-100 d-flex flex-column'>
                                    <div class='mx-4 font-weight-lighter px-3 py-1 text-muted'>
                                        <small class='text-uppercase'>
                                            <Text id='calendarEvent.search-for-animal'>Search for animal</Text>
                                        </small>
                                    </div>
                                    <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-3 px-3 py-3 d-flex flex-column`} style='font-size: 1.1em; border-radius: 10px;'>
                                        <SearchAnimal
                                            stores={this.props.stores}
                                        />
                                    </div>
                                </div>
                            </>}

                            {isNew && <>
                                <div class='w-100 d-flex flex-column'>
                                    <div class='mx-4 font-weight-lighter px-3 py-1 text-muted' >
                                        <small class='text-uppercase'>
                                            <Text id='calendarEvent.search-for-visitor'>Search for customer</Text>
                                        </small>
                                    </div>
                                    <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-3 px-3 py-3 d-flex flex-column`} style='font-size: 1.1em; border-radius: 10px;'>
                                        <SearchVisitor
                                            stores={this.props.stores}
                                        />

                                    </div>
                                </div>
                            </>}


                            {isNew && calendars && calendars.length > 1 && <>
                                <div class='w-100 d-flex flex-column'>
                                    <div class='mx-4 font-weight-lighter px-3 py-1 text-muted'>
                                        <small class='text-uppercase'>
                                            <Text id='calendarEvent.select-calendars'>Book in multiple calendars</Text>
                                        </small>
                                    </div>
                                    <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-3 px-3 py-2 d-flex flex-column`} style='font-size: 1.0em; border-radius: 10px;'>
                                        <div class='form-check mb-1'>
                                            <input
                                                class='form-check-input'
                                                type='checkbox'
                                                id='selectAllCalendars'
                                                checked={allSelected}
                                                onChange={this.toggleAllCalendars}
                                            />
                                            <label class='form-check-label fw-bold' for='selectAllCalendars'>
                                                <Text id='calendarEvent.select-all'>Select all</Text>
                                            </label>
                                        </div>
                                        <hr class='my-1' />
                                        {calendars.map(cal => (
                                            <div class='form-check mb-1'>
                                                <input
                                                    class='form-check-input'
                                                    type='checkbox'
                                                    id={`cal-${cal.calendarId}`}
                                                    value={cal.calendarId}
                                                    checked={selectedCalendarIds.includes(cal.calendarId)}
                                                    onChange={this.toggleCalendarId}
                                                />
                                                <label class='form-check-label' for={`cal-${cal.calendarId}`}>
                                                    {cal.name || cal.calendarId}
                                                </label>
                                            </div>
                                        ))}
                                        {selectedCalendarIds.length > 1 && (
                                            <small class='text-body-secondary mt-1'>
                                                <i class='fa-solid fa-circle-info me-1' />
                                                <Text id='calendarEvent.multi-booking-info'>One event will be created per selected calendar.</Text>
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </>}

                            <div class='w-100 d-flex flex-column justify-content-start'>
                                <div class='bg-light d-flex flex-column justify-content-start h-100 pt-0'>
                                    <FormFields
                                        stores={this.props.stores}
                                        sections={sections}
                                        holdingStore={calendarEventStore}
                                        objectName={isNew ? 'newCalendarEvent' : 'calendarEvent'}
                                        fields={['calendarId', 'title', 'body', 'start', 'end', 'animals', 'backgroundColor', 'skipReminders', 'hasArrived',
                                            'isRecurring', 'recurringStart', 'recurringEnd', 'recurringFrequency', 'smsText', 'dueDate',
                                            'didNotShow', 'didNotShowReason', 'didNotShowNote']}
                                        isNew={isNew}
                                        drawerLevel={drawerLevel}
                                        isAdmin={false}
                                        hideAdmin={true}
                                        hideFilters={true}
                                        lookupArray={lookupArray}
                                        rightHeader={isNew && foundList?.length > 0 ? <span class='text-success font-weight-bold'><i class='fa-solid fa-circle-info' /> Similar calendarEvents ({foundList.length})</span> : null}
                                        rightHeaderClick={this.toggleSimilar}
                                    />
                                </div>
                            </div>
                        </div>
                        {(isNew || showCalendarWhenEditEvent) && !hideCalendarInEventEditor && (
                            <div class='w-100 d-flex flex-column'>
                                <CalendarInline
                                    inputKey={`calendar-inline-${newCalendarEvent?.id}`}
                                    key={`calendar-inline-${newCalendarEvent?.id}`}
                                    stores={this.props.stores}
                                    calendarSelectDateTime={this.calendarSelectDateTime}
                                    startDate={startDate}
                                    showPullDownHandle={true}
                                    pulldownHandleId={id}
                                    showViewEvent={false}
                                    enableSelection={true}
                                />
                            </div>
                        )}
                    </div>
                </div>


            </div>

            {/* Sticky (not fixed) so the bar tracks the drawer's own scroll
                container and stays visible even when the browser is pinch-zoomed
                (position: fixed pins to the layout viewport and disappears
                off-screen when zoomed in - TICKET-01294). Must stay a direct
                sibling of the content wrapper for sticky to work. */}
            {isNew && <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light' style={`position: sticky; bottom: 0; z-index: 5;`}>
                {(!newCalendarEvent.animals || newCalendarEvent.animals.length === 0) && (
                    <div class='alert alert-warning mb-2 py-2' role='alert'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {(newCalendarEvent.visitors && newCalendarEvent.visitors.length > 0) ? (
                            <Text id='calendarEvent.warning-customer-no-animal'>Customer is chosen, but no animal. The customer will not get an automatic SMS reminder until an animal is added to the booking.</Text>
                        ) : (
                            <Text id='calendarEvent.warning-no-animal'>No animal is chosen. No SMS reminder will be sent for this booking, and the calendar will not show who it is for.</Text>
                        )}
                    </div>
                )}
                {dateError && (
                    <div class='alert alert-danger alert-dismissible mb-2' role='alert'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {dateError}
                        <button type='button' class='btn-close' onClick={() => this.setState({ dateError: null })} aria-label='Close'></button>
                    </div>
                )}
                <button type='button' class='btn btn-primary rounded-pill' onClick={this.createCalendarEvent}>
                    {isNew ? <Text id='calendarEvent.create'>Create calendarEvent</Text> : <Text id='calendarEvent.save'>Save calendarEvent</Text>}
                </button>
            </div>}
        </>);
    }
}

export default NewCalendarEvent;
